<template>
  <div class="physical-orders">
    <h2>实物订单管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>实物订单列表</span>
        </div>
      </template>
      <div class="order-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入订单编号、用户昵称、手机号码、商品名称"
            prefix-icon="Search"
            clearable
            class="search-input"
          >
          </el-input>
          <div class="search-filters">
            <el-select v-model="searchForm.status" placeholder="订单状态" clearable>
              <el-option label="待付款" value="pending" />
              <el-option label="待发货" value="paid" />
              <el-option label="待签收" value="shipped" />
              <el-option label="已完成" value="completed" />
              <el-option label="已取消" value="cancelled" />
              <el-option label="已退款" value="refunded" />
            </el-select>
            <el-date-picker
              v-model="searchForm.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="date-picker"
            />
            <el-button type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="orderList"
          stripe
          style="width: 100%"
          class="order-table"
          :row-key="row => row.id"
        >
          <el-table-column label="订单信息" width="400">
            <template #default="scope">
              <div>
                <div><strong>订单编号：</strong>{{ scope.row.order_no }}</div>
                <div><strong>兑换时间：</strong>{{ formatDateTime(null, null, scope.row.created_at) }}</div>
                <div>
                  <strong>状态：</strong>
                  <el-tag :type="getStatusTagType(scope.row.status)">
                    {{ getStatusText(scope.row.status) }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="兑换人信息" width="200">
            <template #default="scope">
              <div>
                <div>昵称：{{ scope.row.user?.nickname || '-' }}</div>
                <div>手机：{{ scope.row.user?.phone || '-' }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="商品信息" width="250">
            <template #default="scope">
              <div class="product-title">
                <template v-for="(item, index) in scope.row.items" :key="index">
                  {{ item.product_name }}{{ index < scope.row.items.length - 1 ? ' ' : '' }}
                </template>
                <template v-if="!scope.row.items || scope.row.items.length === 0">-</template>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="支付信息" width="180">
            <template #default="scope">
              <div>
                <div>实付款：{{ scope.row.total_amount }} 元</div>
                <div>{{ scope.row.points_used }} 积分</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="收货地址" width="250">
            <template #default="scope">
              <div v-if="scope.row.contact_name || scope.row.receiver_name">
                <div>{{ (scope.row.contact_name || scope.row.receiver_name) }} {{ (scope.row.contact_phone || scope.row.receiver_phone) }}</div>
                <div class="address-text">
                  {{ (scope.row.province_name || '') }} {{ (scope.row.city_name || '') }} {{ (scope.row.district_name || '') }} {{ (scope.row.detail_address || scope.row.address || scope.row.receiver_address || '') }}
                </div>
              </div>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="300" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="showOrderDetail(scope.row)">
                <el-icon><View /></el-icon>
                详情
              </el-button>
              <el-button
                v-if="scope.row.status === 'paid'"
                type="warning"
                size="small"
                @click="showShipDialog(scope.row)"
              >
                <el-icon><Box /></el-icon>
                发货
              </el-button>
              <el-button
                v-if="scope.row.refund_status === 'applying'"
                type="danger"
                size="small"
                @click="showRefundDialog(scope.row)"
              >
                <el-icon><Money /></el-icon>
                退款
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="订单详情"
      width="70%"
    >
      <div v-if="currentOrder" class="order-detail-content">
        <el-descriptions title="基本信息" :column="2" border>
          <el-descriptions-item label="订单编号">{{ currentOrder.order_no }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag :type="getStatusTagType(currentOrder.status)">
              {{ getStatusText(currentOrder.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="兑换时间">{{ formatDateTime(null, null, currentOrder.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="支付金额">{{ currentOrder.total_amount }} 元</el-descriptions-item>
          <el-descriptions-item label="支付积分">{{ currentOrder.points_used }} 积分</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="用户信息" :column="2" border style="margin-top: 20px">
          <el-descriptions-item label="用户昵称">{{ currentOrder.user?.nickname }}</el-descriptions-item>
          <el-descriptions-item label="手机号码">{{ currentOrder.user?.phone }}</el-descriptions-item>
          <el-descriptions-item label="会员等级">{{ currentOrder.user?.member_level?.level_name || currentOrder.user?.member_level || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="商品信息" :column="1" border style="margin-top: 20px">
          <el-descriptions-item v-for="(item, index) in currentOrder.items" :key="index" :label="`商品 ${index + 1}`">
            <div class="product-item">
              <el-image
                :src="item.product_image"
                fit="cover"
                style="width: 80px; height: 80px; margin-right: 15px"
              />
              <div class="product-details">
                <div class="product-name">{{ item.product_name }}</div>
                <div class="product-sku" v-if="item.sku_name">{{ item.sku_name }}</div>
                <div class="product-intro" v-if="item.product_intro">{{ item.product_intro }}</div>
                <div class="product-quantity">数量：{{ item.quantity }}</div>
                <div class="product-price">单价：{{ item.unit_price }} 元</div>
                <div class="product-subtotal">小计：{{ item.subtotal }} 元</div>
              </div>
            </div>
          </el-descriptions-item>
          <el-descriptions-item v-if="!currentOrder.items || currentOrder.items.length === 0" label="商品信息">
            无商品信息
          </el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="收货地址" :column="1" border style="margin-top: 20px">
          <el-descriptions-item label="收货人">
            {{ currentOrder.contact_name || currentOrder.receiver_name || '-' }}
            <span v-if="currentOrder.contact_phone || currentOrder.receiver_phone" style="margin-left: 10px">{{ currentOrder.contact_phone || currentOrder.receiver_phone }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="收货地址">
            {{ (currentOrder.province_name || '') }} {{ (currentOrder.city_name || '') }} {{ (currentOrder.district_name || '') }} {{ (currentOrder.detail_address || '') }}
            <span v-if="!currentOrder.detail_address && (currentOrder.address || currentOrder.receiver_address)">{{ currentOrder.address || currentOrder.receiver_address }}</span>
            <span v-if="!currentOrder.detail_address && !currentOrder.address && !currentOrder.receiver_address">-</span>
          </el-descriptions-item>
          <el-descriptions-item v-if="currentOrder.delivery_method" label="配送方式">
            {{ currentOrder.delivery_method === 'delivery' || currentOrder.delivery_method === 'shipping' ? '快递配送' : currentOrder.delivery_method === 'pickup' ? '到店自提' : currentOrder.delivery_method }}
          </el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="currentOrder.shipping_type || currentOrder.tracking_number || currentOrder.express_company" title="物流信息" :column="2" border style="margin-top: 20px">
          <el-descriptions-item label="物流公司">{{ currentOrder.express_company || currentOrder.logistics_company || '-' }}</el-descriptions-item>
          <el-descriptions-item label="快递单号">
            <div class="tracking-number-container">
              <span>{{ currentOrder.tracking_number || '-' }}</span>
              <el-button v-if="currentOrder.tracking_number" type="text" size="small" @click="viewLogistics" style="margin-left: 10px">
                <el-icon><View /></el-icon>
                查看物流
              </el-button>
            </div>
          </el-descriptions-item>
          <el-descriptions-item label="物流类型">{{ currentOrder.shipping_type || '-' }}</el-descriptions-item>
          <el-descriptions-item label="物流状态">{{ getShippingStatusText(currentOrder.shipping_status) || '-' }}</el-descriptions-item>
          <el-descriptions-item label="发货时间">{{ currentOrder.shipped_at ? formatDateTime(null, null, currentOrder.shipped_at) : '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="currentOrder.refund_status !== 'none'" title="退款信息" :column="2" border style="margin-top: 20px">
          <el-descriptions-item label="退款状态">
            <el-tag :type="getRefundStatusTagType(currentOrder.refund_status)">
              {{ getRefundStatusText(currentOrder.refund_status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="退款原因">{{ currentOrder.refund_reason || '无' }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ currentOrder.refunded_at ? formatDateTime(null, null, currentOrder.refunded_at) : '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- 发货弹窗 -->
    <el-dialog
      v-model="shipDialogVisible"
      title="订单发货"
      width="50%"
      @open="loadAvailableLogistics"
    >
      <el-form :model="shipForm" label-width="120px">
        <el-form-item label="订单编号">
          <span>{{ currentOrder?.order_no }}</span>
        </el-form-item>
        <el-form-item label="收货信息">
          <div>{{ (currentOrder?.contact_name || currentOrder?.receiver_name) }} {{ (currentOrder?.contact_phone || currentOrder?.receiver_phone) }}</div>
          <div>{{ (currentOrder?.province_name || '') }} {{ (currentOrder?.city_name || '') }} {{ (currentOrder?.district_name || '') }} {{ (currentOrder?.detail_address || currentOrder?.address || currentOrder?.receiver_address || '') }}</div>
        </el-form-item>
        <el-form-item label="物流公司" required>
          <el-select v-model="shipForm.delivery_id" placeholder="请选择物流公司" :loading="loadingLogistics">
            <el-option
              v-for="company in availableLogistics"
              :key="company.id"
              :value="company.delivery_id"
              :label="company.delivery_name"
            >
              <template #default>
                <div class="company-option-container">
                  <span class="company-name">{{ company.delivery_name }}</span>
                  <span v-if="company.has_monthly_settlement" class="monthly-badge">月</span>
                </div>
              </template>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="保价">
          <el-switch v-model="shipForm.is_insured" @change="handleInsuredChange">
            <template #default>
              <span>{{ shipForm.is_insured ? '是' : '否' }}</span>
            </template>
          </el-switch>
        </el-form-item>
        <el-form-item
          v-if="shipForm.is_insured"
          label="保价金额"
          required
        >
          <el-input
            v-model.number="shipForm.insured_amount"
            placeholder="请输入保价金额（单位：元）"
            type="number"
            :min="1"
            :max="100000"
          >
            <template #append>
              <span>元</span>
            </template>
          </el-input>
          <div class="form-tip">保价金额为货物的实际价值，建议如实填写</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="shipDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleShip" :disabled="!shipForm.delivery_id || (shipForm.is_insured && !shipForm.insured_amount)" :loading="shippingLoading">
          确认发货
        </el-button>
      </template>
    </el-dialog>

    <!-- 退款处理弹窗 -->
    <el-dialog
      v-model="refundDialogVisible"
      title="退款处理"
      width="50%"
    >
      <el-form :model="refundForm" label-width="120px">
        <el-form-item label="订单编号">
          <span>{{ currentOrder?.order_no }}</span>
        </el-form-item>
        <el-form-item label="退款金额">
          <span>{{ currentOrder?.total_amount }} 元</span>
        </el-form-item>
        <el-form-item label="退还积分">
          <span>{{ currentOrder?.points_used }} 积分</span>
        </el-form-item>
        <el-form-item label="退款原因">
          <span>{{ currentOrder?.refund_reason || '用户申请退款' }}</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refundDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleRefund(true)">确认退款</el-button>
        <el-button type="info" @click="handleRefund(false)">拒绝退款</el-button>
      </template>
    </el-dialog>

    <!-- 物流详情弹窗 -->
    <el-dialog
      v-model="logisticsDialogVisible"
      title="物流详情"
      width="60%"
    >
      <div v-if="logisticsLoading" class="loading-container">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <span>加载物流信息中...</span>
      </div>
      <div v-else-if="logisticsError" class="error-container">
        <el-icon class="error-icon"><Warning /></el-icon>
        <span>{{ logisticsError }}</span>
      </div>
      <div v-else-if="logisticsInfo && logisticsInfo.traces" class="logistics-info">
        <div class="logistics-header">
          <div class="logistics-company">{{ logisticsInfo.shipper_name || '物流公司' }}</div>
          <div class="logistics-number">快递单号：{{ logisticsInfo.waybill_id || '-' }}</div>
        </div>
        <div class="logistics-traces">
          <div v-for="(trace, index) in logisticsInfo.traces" :key="index" class="trace-item" :class="{ 'first': index === 0 }">
            <div class="trace-time">{{ trace.occur_time || '-' }}</div>
            <div class="trace-content">{{ trace.occur_desc || '-' }}</div>
          </div>
        </div>
      </div>
      <div v-else class="empty-container">
        <el-icon class="empty-icon"><InfoFilled /></el-icon>
        <span>暂无物流信息</span>
      </div>
      <template #footer>
        <el-button @click="logisticsDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, View, Box, Money, Loading, Warning, InfoFilled } from '@element-plus/icons-vue';
import * as orderApi from '../../api/orders';

export default {
  name: 'PhysicalOrders',
  components: {
    Search,
    View,
    Box,
    Money,
    Loading,
    Warning,
    InfoFilled
  },
  setup() {
    const searchForm = reactive({
      keyword: '',
      status: '',
      dateRange: []
    });

    const loading = ref(false);
    const orderList = ref([]);
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });

    const detailDialogVisible = ref(false);
    const currentOrder = ref(null);

    const shipDialogVisible = ref(false);
    const shipForm = reactive({
      delivery_id: '',
      is_insured: false,
      insured_amount: 0
    });

    const refundDialogVisible = ref(false);
    const refundForm = reactive({
      approve: true,
      reason: ''
    });

    // 可用物流公司相关
    const availableLogistics = ref([]);
    const loadingLogistics = ref(false);
    const shippingLoading = ref(false);

    // 物流详情相关
    const logisticsDialogVisible = ref(false);
    const logisticsLoading = ref(false);
    const logisticsError = ref('');
    const logisticsInfo = ref(null);

    const getStatusText = (status) => {
      const statusMap = {
        pending: '待付款',
        pending_payment: '待付款',
        paid: '待发货',
        shipped: '待签收',
        completed: '已完成',
        done: '已完成',
        cancelled: '已取消',
        refunded: '已退款'
      };
      return statusMap[status] || status;
    };

    const getStatusTagType = (status) => {
      const typeMap = {
        pending: 'info',
        pending_payment: 'info',
        paid: 'warning',
        shipped: 'primary',
        completed: 'success',
        done: 'success',
        cancelled: 'info',
        refunded: 'danger'
      };
      return typeMap[status] || 'info';
    };

    const getRefundStatusText = (status) => {
      const statusMap = {
        none: '无',
        applying: '申请中',
        refunded: '已退款',
        rejected: '已拒绝'
      };
      return statusMap[status] || status;
    };

    const getRefundStatusTagType = (status) => {
      const typeMap = {
        none: 'info',
        applying: 'warning',
        approved: 'success',
        rejected: 'danger'
      };
      return typeMap[status] || 'info';
    };

    const getShippingStatusText = (status) => {
      const statusMap = {
        not_shipped: '未发货',
        shipped: '已发货',
        delivered: '已送达',
        returned: '已退回'
      };
      return statusMap[status] || status;
    };



    // 格式化日期时间为ymdhis标准格式 (YYYY-MM-DD HH:mm:ss)
    const formatDateTime = (row, column, cellValue) => {
      if (!cellValue) return '';
      const date = new Date(cellValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const loadOrders = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          search: searchForm.keyword,
          status: searchForm.status,
          product_type: 'physical'
        };
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.start_date = searchForm.dateRange[0];
          params.end_date = searchForm.dateRange[1];
        }
        console.log('发送请求参数:', params);
        const response = await orderApi.fetchOrdersList(params);
        console.log('API响应:', response);
        console.log('API响应数据结构:', response.data ? Object.keys(response.data) : '无data');
        if (response.code === 200) {
          orderList.value = response.data.data;
          pagination.total = response.data.total;
          console.log('orderList:', orderList.value);
          console.log('第一条订单:', orderList.value[0]);
          console.log('第一条订单的product_type:', orderList.value[0]?.product_type);
        } else {
          ElMessage.error('获取订单列表失败');
        }
      } catch (error) {
        ElMessage.error('获取订单列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.currentPage = 1;
      loadOrders();
    };

    const showOrderDetail = (order) => {
      currentOrder.value = order;
      detailDialogVisible.value = true;
    };

    const showShipDialog = (order) => {
      currentOrder.value = order;
      // 重置表单数据
      shipForm.delivery_id = '';
      shipForm.is_insured = false;
      shipForm.insured_amount = 0;
      shipDialogVisible.value = true;
    };

    // 加载可用物流公司
    const loadAvailableLogistics = async () => {
      if (!currentOrder.value) return;
      
      loadingLogistics.value = true;
      try {
        const response = await orderApi.getAvailableLogistics(currentOrder.value.id);
        if (response.code === 200) {
          availableLogistics.value = response.data;
        } else {
          ElMessage.error('获取物流公司列表失败');
        }
      } catch (error) {
        ElMessage.error('获取物流公司列表失败');
        console.error(error);
      } finally {
        loadingLogistics.value = false;
      }
    };

    // 保价开关变化处理
    const handleInsuredChange = (value) => {
      if (!value) {
        shipForm.insured_amount = 0;
      }
    };

    const handleShip = async () => {
      try {
        // 准备保价信息
        const insuredInfo = {
          is_insured: shipForm.is_insured,
          insured_amount: shipForm.is_insured ? Math.round(shipForm.insured_amount * 100) : 0, // 转换为分
        };
        
        // 合并发货数据和保价信息
        const shipData = {
          ...shipForm,
          ...insuredInfo
        };
        
        const response = await orderApi.shipOrder(currentOrder.value.id, shipData);
        if (response.code === 200) {
          ElMessage.success('发货成功');
          shipDialogVisible.value = false;
          loadOrders();
        } else {
          ElMessage.error('发货失败');
        }
      } catch (error) {
        ElMessage.error('发货失败');
        console.error(error);
      }
    };

    const showRefundDialog = (order) => {
      currentOrder.value = order;
      refundDialogVisible.value = true;
    };

    const handleRefund = async (approve) => {
      try {
        const response = await orderApi.processRefund(currentOrder.value.id, {
          approve,
          reason: approve ? '退款处理' : '拒绝退款'
        });
        if (response.code === 200) {
          ElMessage.success(approve ? '退款处理成功' : '已拒绝退款');
          refundDialogVisible.value = false;
          loadOrders();
        } else {
          ElMessage.error('退款处理失败');
        }
      } catch (error) {
        ElMessage.error('退款处理失败');
        console.error(error);
      }
    };

    // 查看物流
    const viewLogistics = async () => {
      if (!currentOrder.value) return;
      
      logisticsLoading.value = true;
      logisticsError.value = '';
      logisticsInfo.value = null;
      
      try {
        const response = await orderApi.getLogisticsTracking(currentOrder.value.id);
        if (response.code === 200) {
          logisticsInfo.value = response.data;
          logisticsDialogVisible.value = true;
        } else {
          logisticsError.value = '获取物流信息失败';
          logisticsDialogVisible.value = true;
        }
      } catch (error) {
        logisticsError.value = '获取物流信息失败';
        logisticsDialogVisible.value = true;
        console.error(error);
      } finally {
        logisticsLoading.value = false;
      }
    };

    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadOrders();
    };

    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadOrders();
    };

    onMounted(() => {
      loadOrders();
    });

    return {
      searchForm,
      loading,
      orderList,
      pagination,
      detailDialogVisible,
      currentOrder,
      shipDialogVisible,
      shipForm,
      refundDialogVisible,
      refundForm,
      availableLogistics,
      loadingLogistics,
      shippingLoading,
      logisticsDialogVisible,
      logisticsLoading,
      logisticsError,
      logisticsInfo,
      getStatusText,
      getStatusTagType,
      getRefundStatusText,
      getRefundStatusTagType,
      getShippingStatusText,
      formatDateTime,
      loadOrders,
      handleSearch,
      showOrderDetail,
      showShipDialog,
      loadAvailableLogistics,
      handleShip,
      showRefundDialog,
      handleRefund,
      viewLogistics,
      handleSizeChange,
      handleCurrentChange
    };
  }
};
</script>

<style scoped>
.physical-orders {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 450px;
}

.search-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.date-picker {
  width: 300px;
}

.order-list-content {
  margin-top: 20px;
}

.order-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.product-info {
  display: flex;
  align-items: center;
}

.product-detail {
  flex: 1;
}

.product-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-intro {
  font-size: 12px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}

.address-text {
  font-size: 12px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.product-item {
  display: flex;
  align-items: flex-start;
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-sku {
  font-size: 12px;
  color: #666;
  margin-bottom: 3px;
}

.product-intro {
  font-size: 12px;
  color: #999;
  margin-bottom: 3px;
  line-height: 1.4;
}

.product-quantity,
.product-price,
.product-subtotal {
  font-size: 12px;
  color: #666;
  margin-top: 3px;
}

.order-detail-content {
  max-height: 600px;
  overflow-y: auto;
}

/* Company option with monthly badge */
.company-option-container {
  position: relative;
  display: inline-block;
  padding-right: 16px;
}

.monthly-badge {
  position: absolute;
  top: 2px;
  right: 0px;
  background-color: #4377be;
  color: white;
  font-size: 10px;
  padding: 3px;
  border-radius: 100%;
  line-height: 1;
  font-weight: bold;
}

/* 物流信息样式 */
.tracking-number-container {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.logistics-info {
  padding: 10px 0;
}

.logistics-header {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.logistics-company {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 5px;
}

.logistics-number {
  font-size: 14px;
  color: #606266;
}

.logistics-traces {
  position: relative;
  padding-left: 20px;
}

.logistics-traces::before {
  content: '';
  position: absolute;
  left: 7px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e4e7ed;
}

.trace-item {
  position: relative;
  padding-bottom: 20px;
  padding-left: 20px;
}

.trace-item::before {
  content: '';
  position: absolute;
  left: -13px;
  top: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #c0c4cc;
  border: 2px solid #e4e7ed;
}

.trace-item.first::before {
  background-color: #409eff;
  border-color: #409eff;
}

.trace-time {
  font-size: 14px;
  color: #909399;
  margin-bottom: 5px;
}

.trace-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.5;
}

.loading-container,
.error-container,
.empty-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #909399;
}

.loading-icon,
.error-icon,
.empty-icon {
  font-size: 24px;
  margin-right: 10px;
}

.loading-icon {
  animation: rotate 1s linear infinite;
}

.error-icon {
  color: #f56c6c;
}

.empty-icon {
  color: #909399;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>